import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import {
  AGENT_CHANNEL_INDEX,
  PERSONA_CHANNEL_INDEX,
} from "@/components/audio-player/useAudioWaveformOptions"
import { messageSpeaker } from "@/components/transcript/messageSpeaker"
import { getMessageInterval } from "@/components/transcript/TimestampDisplay"
import type { Message } from "@/types/Transcript"

/** The one token tuned to read on either speaker's lane, in either theme. */
const REFERENCED_MESSAGE_REGION_COLOR = "var(--coval-media-region-highlight)"

/** A message whose interval is a single instant still needs a visible span. */
const MINIMUM_REGION_SECONDS = 0.25

/** The lane of the split waveform each side of the conversation is drawn in. */
const CHANNEL_INDEX_BY_SPEAKER = {
  agent: AGENT_CHANNEL_INDEX,
  persona: PERSONA_CHANNEL_INDEX,
} as const

/**
 * The stretch of audio one referenced message was spoken in, marked in its own
 * speaker's lane so the span sits on the wave that carries it. Mono audio has
 * only one lane, and Wavesurfer falls back to the full height there.
 *
 * A message the transcript records no timing for — a text conversation, or a
 * turn that stored only one end of its interval — has no such stretch, so the
 * waveform is left alone.
 */
export function buildReferencedMessageAudioRegion({
  message,
  messageIndex,
  metricId,
}: {
  message: Message | undefined
  messageIndex: number
  metricId: string
}): AudioRegionParams | null {
  const interval = message ? getMessageInterval(message) : null
  if (!message || !interval) {
    return null
  }

  return {
    id: `referenced-message:${metricId}:${messageIndex}`,
    start: interval.start,
    end:
      interval.end > interval.start
        ? interval.end
        : interval.start + MINIMUM_REGION_SECONDS,
    color: REFERENCED_MESSAGE_REGION_COLOR,
    channelIdx: CHANNEL_INDEX_BY_SPEAKER[messageSpeaker(message)],
    drag: false,
    resize: false,
  }
}
